import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { IconClose, IconBuilding } from './Icons';
import SsmateAgenteAnim from './SsmateAgenteAnim';
import './phases.css';

const nf = (n) => (n || 0).toLocaleString('it-IT');
const due2 = (n) => String(n).padStart(2, '0');
// Conto alla rovescia leggibile fino al momento target (ms rimanenti -> "2g 3h", "1h 05m", "04:12")
const fmtCountdown = (ms) => {
  if (ms == null || ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const g = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (g > 0) return `${g}g ${h}h`;
  if (h > 0) return `${h}h ${due2(m)}m`;
  return `${due2(m)}:${due2(sec)}`;
};
const LUCCHETTO_HEX = {
  GRIGIO: '#9ca3af', VERDE: '#22c55e', NERO: '#111827', VIOLA: '#a855f7',
  ROSSO: '#ef4444', GIALLO: '#eab308', ARANCIONE: '#f97316', 'NON TROVATO': '#64748b',
};

/* Dialog dell'agente SSMATE: animazione + statistiche del job massivo.
   Gemello di CoperturaStatsDialog ma con i dati del presidio commerciale. */
export default function SsmateStatsDialog({ open, onClose }) {
  const [info, setInfo] = useState(null);
  const [closing, setClosing] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());   // tick per il countdown
  const [ultimoLuc, setUltimoLuc] = useState(null);
  const [flash, setFlash] = useState(0);   // scatta ad ogni CF verificato (sincronizza il badge)
  const prevLucRef = useRef(null);

  // ESITO REALE: quale lucchetto e' cresciuto -> flash sincronizzato
  useEffect(() => {
    const l = info?.job?.lucchetti;
    if (!l) return;
    const prev = prevLucRef.current;
    if (prev) {
      const grown = Object.keys(l).find((k) => (l[k] || 0) > (prev[k] || 0));
      if (grown) { setUltimoLuc({ label: grown, color: LUCCHETTO_HEX[grown] || '#94a3b8' }); setFlash((f) => f + 1); }
    }
    prevLucRef.current = { ...l };
  }, [info]);

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { setClosing(false); onClose?.(); }, 320);
  };

  useEffect(() => {
    if (!open) return;
    setClosing(false);
    setConfirmStop(false);
    const load = () => axios.get(`${API}/ssmate/bulk/preview`).then((r) => setInfo(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 1500);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { clearInterval(id); clearInterval(tick); };
  }, [open]);

  const ferma = async () => {
    if (!info?.job?.id) return;
    setStopping(true);
    try { await axios.post(`${API}/ssmate/job/${info.job.id}/cancel`); toast.info('AI SSMATE fermato'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setStopping(false);
    setConfirmStop(false);
  };

  if (!open) return null;

  const job = info?.job;
  const running = job && ['running', 'pending'].includes(job.status);
  const pct = job && job.total ? Math.round((100 * job.completed) / job.total) : 0;

  return createPortal(
    <div className={`bulk-overlay${closing ? ' closing' : ''}`}>
      <div className="bulk-dialog">
        <div className="bulk-bg">
          <div className="bulk-grid" />
          {running && (
            <div className="bulk-radar">
              <span className="bulk-ring r0" /><span className="bulk-ring r1" />
              <span className="bulk-ring r2" /><span className="bulk-ring r3" />
            </div>
          )}
        </div>

        <div className="bulk-content">
          <div className="bulk-head">
            <span className="bulk-title">AI SSMATE</span>
            <button className="bulk-x" onClick={handleClose}><IconClose size={16} color="#fff" /></button>
          </div>

          {!running ? (
            (() => {
              const fascia = info?.agente_attivo && info?.in_attesa
                && info?.motivo_attesa === 'fuori fascia oraria';
              const target = fascia && info?.prossima_fascia ? new Date(info.prossima_fascia) : null;
              const cd = target ? fmtCountdown(target.getTime() - nowMs) : null;
              if (fascia) {
                return (
                  <div className="bulk-empty">
                    <div className="bulk-empty-ico" style={{ fontSize: 30 }}>🕐</div>
                    <p>Fermo per la programmazione</p>
                    <p style={{ fontSize: 12, opacity: .75 }}>Fuori dalla fascia oraria impostata</p>
                    {target && (
                      <>
                        <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: 1, margin: '6px 0 2px' }}>
                          {cd || 'a breve'}
                        </div>
                        <p style={{ fontSize: 11, opacity: .6 }}>
                          Riprende {target.toLocaleString('it-IT', {
                            weekday: 'short', hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </>
                    )}
                    {info?.verificabili != null && (
                      <p style={{ fontSize: 12, opacity: .7, marginTop: 6 }}>In coda: <b>{nf(info.verificabili)}</b></p>
                    )}
                  </div>
                );
              }
              return (
                <div className="bulk-empty">
                  <div className="bulk-empty-ico"><IconBuilding size={32} color="#cbd5e1" /></div>
                  <p>AI SSMATE fermo</p>
                  {info?.verificabili != null && (
                    <p style={{ fontSize: 12, opacity: .7 }}>Verificabili: <b>{nf(info.verificabili)}</b></p>
                  )}
                  {info?.esauriti?.length > 0 && (
                    <p style={{ fontSize: 11, opacity: .6 }}>Esauriti oggi: {info.esauriti.join(', ')}</p>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="ph-wrap">
              <SsmateAgenteAnim attivo={running} standby={job?.status === 'standby'} esito={ultimoLuc} flash={flash} />
              <div className="ph-label">Verifica presidio commerciale</div>
              <div className="ph-sub">
                {job.current ? <>verifico: <span className="ph-num">{job.current}</span></> : 'preparazione…'}
                {job.current_user ? <> · {job.current_user}</> : null}
              </div>

              <div className="ph-stats">
                <div className="ph-stats-head">
                  <span>{nf(job.completed)}/{nf(job.total)} ({pct}%)</span>
                  {job.quota_stopped && <span style={{ opacity: .8 }}>quota esaurita</span>}
                </div>
                <div className="ph-stack">
                  <span style={{ width: `${pct}%`, background: '#3b82f6' }} />
                </div>
                <div className="ph-legend">
                  <span><i style={{ background: '#22c55e' }} />Verificati {nf(job.verificati)}</span>
                  <span><i style={{ background: '#94a3b8' }} />Saltati {nf(job.saltati)}</span>
                  <span><i style={{ background: '#ef4444' }} />Errori {nf(job.errori)}</span>
                </div>
                {Object.keys(job.lucchetti || {}).length > 0 && (
                  <div className="ph-legend" style={{ flexWrap: 'wrap', marginTop: 4 }}>
                    {Object.entries(job.lucchetti).map(([k, v]) => (
                      <span key={k}><i style={{ background: LUCCHETTO_HEX[k] || '#94a3b8' }} />{k} {nf(v)}</span>
                    ))}
                  </div>
                )}
                {job.site_status && (
                  <div className="ph-speed" style={{ borderTop: 'none' }}><span>{job.site_status}</span></div>
                )}
              </div>

              {confirmStop ? (
                <div className="bulk-confirm">
                  <span className="bulk-confirm-q">Fermare l'agente SSMATE?</span>
                  <div className="bulk-confirm-btns">
                    <button className="bulk-cancel" onClick={ferma} disabled={stopping}>{stopping ? '…' : 'Sì'}</button>
                    <button className="bulk-keep" onClick={() => setConfirmStop(false)} disabled={stopping}>No</button>
                  </div>
                </div>
              ) : (
                <button className="bulk-cancel" onClick={() => setConfirmStop(true)}>Ferma agente</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
